// Helpers for a school-event item on the in-app calendars (#256).
import { formatInSchoolTz } from '../../utils/dateUtils';

const DAY = { day: '2-digit', month: '2-digit', year: 'numeric' } as const;
const TIME = { hour: 'numeric', minute: '2-digit', hour12: true } as const;

/**
 * "06/12/2026, 7:00 pm – 9:00 pm", or both days when the event spans several. Read from the REAL
 * instants in the SCHOOL's zone, never from the fake-local `start`/`end`.
 */
export function formatEventWhen(
  startsAtIso: string | undefined,
  endsAtIso: string | undefined,
  timeZone: string
): string {
  if (!startsAtIso) return '';
  const startDay = formatInSchoolTz(startsAtIso, timeZone, DAY);
  const startTime = formatInSchoolTz(startsAtIso, timeZone, TIME);
  if (!endsAtIso) return `${startDay}, ${startTime}`;
  const endDay = formatInSchoolTz(endsAtIso, timeZone, DAY);
  const endTime = formatInSchoolTz(endsAtIso, timeZone, TIME);
  return startDay === endDay
    ? `${startDay}, ${startTime} – ${endTime}`
    : `${startDay} ${startTime} – ${endDay} ${endTime}`;
}

/**
 * The time a calendar chip shows for THIS day of an event (Day strip), 12-hour en-AU:
 * first day = start time, last day = "until <end>", a day between = "All day".
 * `dayKey` and the keys are school-day YYYY-MM-DD, which compare as strings.
 */
export function eventTimeLabelForDay(
  dayKey: string,
  startDateKey: string | undefined,
  endDateKey: string | undefined,
  startTime: string | undefined,
  endTime: string | undefined
): string {
  const to12 = (hhmm?: string) => {
    if (!hhmm) return '';
    const [h, m] = hhmm.split(':').map(Number);
    return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'pm' : 'am'}`;
  };
  const startsToday = !startDateKey || dayKey <= startDateKey;
  const endsToday = !!endDateKey && dayKey >= endDateKey;
  if (startsToday) return to12(startTime);
  if (endsToday) return `until ${to12(endTime)}`;
  return 'All day';
}
